import { useState, useEffect, useCallback } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { ArrowLeft, Plus, Trash2, GripVertical, Save, ChevronDown, Clock, Users, FileText, CheckSquare, Mail, Send, Check, Loader2, Pencil, Search } from "lucide-react"
import { useToast } from "@/components/ui/use-toast"
import { CustomFieldValue, PlaybookTemplate } from "@/types/customer-success"
import CustomFieldsEditor from "@/components/customer-success/playbooks/CustomFieldsEditor"
import { playbookService } from "@/services/playbooks"
import debounce from "lodash/debounce"

export default function EditPlaybookTemplate() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [taskGroups, setTaskGroups] = useState<any[]>([])
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false)
  const [newGroupTitle, setNewGroupTitle] = useState("")
  const [newGroupIcon, setNewGroupIcon] = useState("🎯")
  const [isLoading, setIsLoading] = useState(true)
  const [users, setUsers] = useState<any[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [selectedTask, setSelectedTask] = useState<{groupId: string, taskId: string, task: any} | null>(null)
  
  // Load the template data
  useEffect(() => {
    const fetchTemplateData = async () => {
    if (!id) return
    
    setIsLoading(true)
    
      try {
        const template = await playbookService.templates.getById(id) as PlaybookTemplate & { template_content?: string }
        
        if (template) {
          setName(template.name)
          setDescription(template.description || "")
          
          let jsonGroups: any[] = [];
      
      // Parse template content to get task groups
          if (template.template_content) {
            try {
              console.log("Template content carregado:", template.template_content)
              const content = JSON.parse(template.template_content)
              console.log("Template content parseado:", content)
              jsonGroups = content.groups.map((group: any) => ({
                id: group.id || crypto.randomUUID(),
            title: group.title,
            icon: group.icon,
            tasks: group.tasks.map((task: any) => ({
                  id: task.id || crypto.randomUUID(),
              title: task.title,
              description: task.description || "",
              type: task.task_type || "task",
              assignee_type: "fixed",
              assignees: task.assignees || [],
              deadline_type: task.deadline_type || "days",
              deadline_value: task.deadline_value || 2,
              deadline_reference: task.deadline_reference || "launch",
              detailed_description: task.detailed_description || "",
              custom_fields: task.custom_fields || [] as CustomFieldValue[]
            }))
          }))
        } catch (error) {
          console.error("Error parsing template content:", error)
            }
          } else {
            console.log("Template não tem conteúdo")
          }
          
          // Tentar carregar os grupos da tabela também
          try {
            console.log("Buscando grupos da tabela para o template:", id)
            const tableGroups = await playbookService.templateTaskGroups.getByTemplateId(id)
            console.log("Grupos encontrados na tabela:", tableGroups)
            
            if (tableGroups && tableGroups.length > 0) {
              // Se temos grupos da tabela e do JSON, vamos carregar também as tarefas da tabela
              const groupsWithTasks = [];
              
              for (const group of tableGroups) {
                // Buscar as tarefas deste grupo
                const tasks = await playbookService.templateTasks.getByGroupId(group.id);
                console.log(`Tarefas encontradas para o grupo ${group.title}:`, tasks);
                
                groupsWithTasks.push({
                  id: group.id,
                  title: group.title,
                  icon: group.icon,
                  tasks: tasks.map(task => ({
                    id: task.id,
                    title: task.title,
                    description: task.description || "",
                    type: task.task_type || "task",
                    assignee_type: "fixed",
                    assignees: task.assignees || [],
                    deadline_type: task.deadline_type || "days",
                    deadline_value: task.deadline_value || 2,
                    deadline_reference: task.deadline_reference || "launch",
                    detailed_description: task.detailed_description || "",
                    custom_fields: task.custom_fields || [] as CustomFieldValue[]
                  }))
                });
              }
              
              // Ordenar por group_order
              groupsWithTasks.sort((a, b) => {
                const groupA = tableGroups.find(g => g.id === a.id);
                const groupB = tableGroups.find(g => g.id === b.id);
                if (!groupA || !groupB) return 0;
                return (groupA.group_order || 0) - (groupB.group_order || 0);
              });
              
              console.log("Grupos com tarefas carregados da tabela:", groupsWithTasks);
              
              // Se temos grupos carregados da tabela, usar eles
              if (jsonGroups.length > 0) {
                // Se temos grupos do JSON, priorizar os da tabela que são mais detalhados
                console.log("Usando grupos da tabela no lugar dos do JSON");
                setTaskGroups(groupsWithTasks);
              } else {
                // Se só temos grupos da tabela, usá-los
                setTaskGroups(groupsWithTasks);
              }
            } else {
              // Se não temos grupos da tabela, usar os do JSON
              setTaskGroups(jsonGroups)
            }
          } catch (error) {
            console.error("Erro ao buscar grupos da tabela:", error)
            // Se falhar ao buscar da tabela, usar os grupos do JSON
            setTaskGroups(jsonGroups)
      }
    } else {
      toast({
        title: "Template não encontrado",
        description: "O template solicitado não foi encontrado.",
        variant: "destructive"
      })
      navigate("/customer-success/playbooks")
    }
      } catch (error) {
        console.error("Error fetching template:", error)
        toast({
          title: "Erro ao carregar template",
          description: "Não foi possível carregar os dados do template.",
          variant: "destructive"
        })
        navigate("/customer-success/playbooks")
      } finally {
    setIsLoading(false)
      }
    }
    
    fetchTemplateData()
  }, [id, navigate, toast])
  
  // Carregar usuários
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await playbookService.users.getAll();
        setUsers(users);
      } catch (error) {
        console.error("Erro ao carregar usuários:", error);
      }
    };
    loadUsers();
  }, []);
  
  const handleAddGroup = () => {
    if (newGroupTitle.trim()) {
      setTaskGroups([
        ...taskGroups,
        {
          id: crypto.randomUUID(),
          title: newGroupTitle,
          icon: newGroupIcon,
          tasks: []
        }
      ])
      setNewGroupTitle("")
      setNewGroupIcon("🎯")
      setShowAddGroupDialog(false)
    }
  }
  
  const handleAddTask = (groupId: string) => {
    const newTaskId = crypto.randomUUID();
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: [
            ...group.tasks,
            {
              id: newTaskId,
              title: "Nova tarefa",
              description: "",
              type: "task",
              assignee_type: "fixed",
              assignees: [],
              deadline_type: "days",
              deadline_value: 2,
              deadline_reference: "launch",
              detailed_description: "",
              custom_fields: []
            }
          ]
        }
      }
      return group;
    }));
    setHasUnsavedChanges(true);
  }
  
  const handleRemoveTask = (groupId: string, taskId: string) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: group.tasks.filter(task => task.id !== taskId)
        }
      }
      return group
    }))
  }
  
  const handleRemoveGroup = (groupId: string) => {
    setTaskGroups(taskGroups.filter(group => group.id !== groupId))
  }
  
  const handleUpdateTask = (groupId: string, taskId: string, field: string, value: any) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          tasks: group.tasks.map(task => {
            if (task.id === taskId) {
              // Se o campo for assignees, garantir que seja um array
              if (field === "assignees") {
                return { ...task, [field]: Array.isArray(value) ? value : [value] };
              }
              return { ...task, [field]: value };
            }
            return task;
          })
        };
      }
      return group;
    }));
    setHasUnsavedChanges(true);
  }
  
  const handleUpdateGroup = (groupId: string, field: string, value: string) => {
    setTaskGroups(taskGroups.map(group => {
      if (group.id === groupId) {
        return { ...group, [field]: value }
      }
      return group
    }))
  }
  
  const handleUpdateCustomFields = (groupId: string, taskId: string, customFields: CustomFieldValue[]) => {
    handleUpdateTask(groupId, taskId, 'custom_fields', customFields)
  }
  
  const handleSaveTemplate = async () => {
    // Validate input
    if (!name.trim()) {
      toast({
        title: "Nome do template é obrigatório",
        description: "Por favor, forneça um nome para o template.",
        variant: "destructive"
      })
      return
    }
    
    // Construct template content
    const templateContent = {
        groups: taskGroups.map(group => ({
        id: group.id,
          title: group.title,
          icon: group.icon,
          tasks: group.tasks.map(task => ({
          id: task.id,
            title: task.title,
            description: task.description,
            task_type: task.type,
            assignee_type: "fixed",
            assignees: task.assignees,
            deadline_type: task.deadline_type,
            deadline_value: task.deadline_value,
            deadline_reference: task.deadline_reference,
            detailed_description: task.detailed_description,
          custom_fields: task.custom_fields
          }))
        }))
      }
    
    console.log("Salvando template content:", templateContent)
    console.log("Salvando template content (JSON):", JSON.stringify(templateContent))
    
    // Save the template
    try {
      // Primeiro salvar o template com o JSON completo
      await playbookService.templates.update(id, {
        name,
        description,
        template_content: JSON.stringify(templateContent)
      } as any)
      
      // Agora limpar e recriar os grupos na tabela para garantir consistência
      try {
        console.log("Recriando grupos na tabela para consistência...");
        
        // Primeiro, excluir todos os grupos existentes deste template
        const existingGroups = await playbookService.templateTaskGroups.getByTemplateId(id);
        for (const group of existingGroups) {
          // Excluir todas as tarefas do grupo antes de excluir o grupo
          await playbookService.templateTasks.deleteByGroupId(group.id);
          await playbookService.templateTaskGroups.delete(group.id);
        }
        
        // Agora recriar todos os grupos na tabela com suas tarefas
        for (let groupIndex = 0; groupIndex < taskGroups.length; groupIndex++) {
          const group = taskGroups[groupIndex];
          
          // Criar o grupo
          const savedGroup = await playbookService.templateTaskGroups.create({
            playbook_template_id: id,
            title: group.title,
            icon: group.icon,
            group_order: groupIndex
          });
          
          // Criar todas as tarefas do grupo
          for (let taskIndex = 0; taskIndex < group.tasks.length; taskIndex++) {
            const task = group.tasks[taskIndex];
            await playbookService.templateTasks.create({
              playbook_template_group_id: savedGroup.id,
              title: task.title,
              description: task.description,
              task_type: task.type,
              assignee_type: "fixed",
              assignees: task.assignees,
              deadline_type: task.deadline_type,
              deadline_value: task.deadline_value,
              deadline_reference: task.deadline_reference,
              detailed_description: task.detailed_description,
              custom_fields: task.custom_fields,
              task_order: taskIndex
            });
          }
        }
        console.log("Grupos e tarefas recriados na tabela com sucesso");
      } catch (error) {
        console.error("Erro ao recriar grupos e tarefas na tabela:", error);
        // Não vamos interromper o fluxo por falha na tabela de grupos
      }
    
    toast({
        title: "Template salvo com sucesso",
        description: "O template foi atualizado com sucesso."
      })
      
      // Navigate back to the templates page
      navigate("/customer-success/playbooks")
    } catch (error) {
      console.error("Error saving template:", error)
      toast({
        title: "Erro ao salvar template",
        description: "Não foi possível salvar o template. Por favor, tente novamente.",
        variant: "destructive"
      })
    }
  }
  
  // Função para obter o nome da tarefa referenciada
  const getTaskReferenceDisplay = (referenceValue: string, currentTaskId: string) => {
    if (referenceValue === 'launch') return 'Início do playbook';
    if (referenceValue === 'previous_task') return 'Tarefa anterior';
    
    if (referenceValue.startsWith('task_')) {
      const refTaskId = referenceValue.replace('task_', '');
      const refTask = taskGroups.flatMap(g => g.tasks).find(t => t.id === refTaskId);
      return refTask ? `"${refTask.title}"` : 'Tarefa específica';
    }
    
    return 'Tarefa específica';
  };
  
  // Função para lidar com o fim do drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    // Se não houver destino ou se o item for solto na mesma posição, não faz nada
    if (!destination || 
        (destination.droppableId === source.droppableId && 
         destination.index === source.index)) {
      return;
    }
    
    // Reordenação de grupos
    if (type === "group") {
      const newGroups = Array.from(taskGroups);
      const [removed] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, removed);
      
      setTaskGroups(newGroups);
      return;
    }
    
    // Reordenação de tarefas dentro do mesmo grupo
    if (source.droppableId === destination.droppableId) {
      const group = taskGroups.find(g => g.id === source.droppableId);
      if (!group) return;
      
      const newTasks = Array.from(group.tasks);
      const [removed] = newTasks.splice(source.index, 1);
      newTasks.splice(destination.index, 0, removed);
      
      setTaskGroups(taskGroups.map(g => {
        if (g.id === source.droppableId) {
          return { ...g, tasks: newTasks };
        }
        return g;
      }));
    } 
    // Movendo tarefas entre grupos diferentes
    else {
      const sourceGroup = taskGroups.find(g => g.id === source.droppableId);
      const destGroup = taskGroups.find(g => g.id === destination.droppableId);
      
      if (!sourceGroup || !destGroup) return;
      
      const sourceTasks = Array.from(sourceGroup.tasks);
      const destTasks = Array.from(destGroup.tasks);
      
      const [removed] = sourceTasks.splice(source.index, 1);
      destTasks.splice(destination.index, 0, removed);
      
      setTaskGroups(taskGroups.map(g => {
        if (g.id === source.droppableId) {
          return { ...g, tasks: sourceTasks };
        }
        if (g.id === destination.droppableId) {
          return { ...g, tasks: destTasks };
        }
        return g;
      }));
    }
  };
  
  // Debounced save function
  const debouncedSave = useCallback(
    debounce(async () => {
      if (!id || !name.trim()) return;
      
      setIsSaving(true);
      try {
        const templateContent = {
          groups: taskGroups.map(group => ({
            id: group.id,
            title: group.title,
            icon: group.icon,
            tasks: group.tasks.map(task => ({
              id: task.id,
              title: task.title,
              description: task.description,
              task_type: task.type,
              assignee_type: "fixed",
              assignees: task.assignees,
              deadline_type: task.deadline_type,
              deadline_value: task.deadline_value,
              deadline_reference: task.deadline_reference,
              detailed_description: task.detailed_description,
              custom_fields: task.custom_fields
            }))
          }))
        };

        await playbookService.templates.update(id, {
          name,
          description,
          template_content: JSON.stringify(templateContent)
        } as any);

        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error("Error auto-saving template:", error);
        toast({
          title: "Erro ao salvar automaticamente",
          description: "Não foi possível salvar as alterações. Por favor, tente novamente.",
          variant: "destructive"
        });
      } finally {
        setIsSaving(false);
      }
    }, 2000),
    [id, name, description, taskGroups, toast]
  );

  // Effect to trigger auto-save when changes are made
  useEffect(() => {
    if (id && name.trim()) {
      setHasUnsavedChanges(true);
      debouncedSave();
    }
  }, [id, name, description, taskGroups, debouncedSave]);

  // Effect to handle beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  if (isLoading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Carregando template...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      {/* Barra de navegação superior com breadcrumbs e ações */}
      <div className="border-b bg-background px-6 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/customer-success/playbooks/template/${id}`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-extrabold">{name || "Template de Playbook"}</h1>
            <div className="flex items-center text-xs text-muted-foreground">
              <Link to="/customer-success/playbooks" className="hover:underline">Playbooks</Link>
              <span className="mx-1">›</span>
              <Link to={`/customer-success/playbooks/template/${id}`} className="hover:underline">Template</Link>
              <span className="mx-1">›</span>
              <span>Editar</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Salvando...</span>
              </>
            ) : lastSaved ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                <span>Salvo às {lastSaved.toLocaleTimeString()}</span>
              </>
            ) : null}
          </div>
          <Button onClick={handleSaveTemplate}>
            <Save className="mr-2 h-4 w-4" />
            Salvar
          </Button>
        </div>
      </div>
      
      {/* Área principal de três painéis */}
      <div className="flex-1 flex overflow-hidden">
        {/* Painel de navegação (esquerda) */}
        <div className="w-64 border-r h-full overflow-y-auto p-4 flex flex-col">
          <div className="mb-4">
            <h2 className="text-sm font-extrabold mb-2">Informações Básicas</h2>
            <div className="text-xs text-muted-foreground mb-1">Nome</div>
            <Input 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              placeholder="Nome do template"
              className="h-8 mb-2" 
            />
            <div className="text-xs text-muted-foreground mb-1">Descrição</div>
            <Textarea 
              value={description} 
              onChange={(e) => setDescription(e.target.value)} 
              placeholder="Descrição breve"
              className="text-sm resize-none"
              rows={2}
            />
          </div>
          
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-extrabold">Grupos de Tarefas</h2>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowAddGroupDialog(true)}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="space-y-1 flex-1 overflow-y-auto">
            {taskGroups.map((group, index) => (
              <div 
                key={group.id} 
                className="flex items-center p-2 rounded-md hover:bg-muted/50 cursor-pointer text-sm"
                onClick={() => {/* Selecionar grupo */}}
              >
                <span className="mr-2">{group.icon}</span>
                <span className="flex-1 truncate">{group.title}</span>
                <div className="text-xs bg-muted px-1.5 rounded-full">{group.tasks.length}</div>
              </div>
            ))}
            
            {taskGroups.length === 0 && (
              <div className="text-xs text-center text-muted-foreground py-4">
                Nenhum grupo adicionado
              </div>
            )}
          </div>
          
          <div className="mt-auto pt-4">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => setShowAddGroupDialog(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Novo Grupo
            </Button>
          </div>
        </div>
        
        {/* Área de conteúdo principal (centro) */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Header da área central com controles de visualização */}
          <div className="border-b p-3 flex items-center justify-between bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="border rounded-md flex overflow-hidden">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-3 rounded-none data-[state=active]:bg-background data-[state=active]:text-foreground" 
                  data-state="active">
                  <FileText className="h-4 w-4 mr-2" />
                  Lista
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar tarefas..."
                  className="w-64 h-8 pl-9"
                />
              </div>
              <Button variant="outline" size="sm" className="h-8">
                <Plus className="h-4 w-4 mr-2" />
                Novo Grupo
              </Button>
            </div>
          </div>
          
          {/* Conteúdo principal com visualização */}
          <div className="flex-1 overflow-auto p-4">
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={() => {}} onBeforeDragStart={() => {}} enableDefaultSensors={true}>
              {taskGroups.map((group, groupIndex) => (
                <Card key={group.id} className="border border-border mb-4 group/card">
                  <CardHeader className="p-3 pb-2 flex flex-row items-center justify-between bg-muted/20">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center">
                        <span className="h-5 w-5 flex items-center justify-center mr-2 text-lg">
                          {group.icon}
                        </span>
                        <h3 className="font-extrabold">{group.title}</h3>
                        <div className="ml-2 bg-muted rounded-full px-2 py-0.5 text-xs">
                          {group.tasks.length}
                        </div>
                      </div>
                      <div className="opacity-0 group-hover/card:opacity-100 transition-opacity ml-2">
                        <Button variant="ghost" size="icon" className="h-7 w-7">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover/card:opacity-100 transition-opacity"
                        onClick={() => handleAddTask(group.id)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 group-hover/card:opacity-100 transition-opacity"
                        onClick={() => handleRemoveGroup(group.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    <Droppable droppableId={group.id} type="task" mode="standard" direction="vertical" isDropDisabled={false}>
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="space-y-2"
                        >
                          <div className="border rounded-md overflow-hidden bg-white">
                            <Table>
                              <TableHeader>
                                <TableRow className="h-9 hover:bg-transparent">
                                  <TableHead className="w-10"></TableHead>
                                  <TableHead>Tarefa</TableHead>
                                  <TableHead className="w-32">Tipo</TableHead>
                                  <TableHead className="w-40">Responsável</TableHead>
                                  <TableHead className="w-56">Prazo</TableHead>
                                  <TableHead className="w-10"></TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.tasks.map((task, taskIndex) => (
                                  <Draggable
                                    key={task.id}
                                    draggableId={task.id}
                                    index={taskIndex}
                                  >
                                    {(provided) => (
                                      <TableRow 
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        className="h-12 group/row hover:bg-muted/30 cursor-pointer"
                                        onClick={() => setSelectedTask({groupId: group.id, taskId: task.id, task})}
                                      >
                                        <TableCell className="p-1 pl-2">
                                          <div {...provided.dragHandleProps}>
                                            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                                          </div>
                                        </TableCell>
                                        <TableCell className="py-1 px-2">
                                          <div className="flex flex-col">
                                            <div className="font-extrabold">{task.title}</div>
                                            {task.description && (
                                              <div className="text-xs text-muted-foreground truncate max-w-xs">
                                                {task.description}
                                              </div>
                                            )}
                                          </div>
                                        </TableCell>
                                        <TableCell className="py-1 px-2">
                                          <div className="flex items-center">
                                            {task.type === 'task' && <CheckSquare className="mr-2 h-4 w-4 text-blue-500" />}
                                            {task.type === 'approval' && <Users className="mr-2 h-4 w-4 text-purple-500" />}
                                            {task.type === 'email_draft' && <Mail className="mr-2 h-4 w-4 text-orange-500" />}
                                            {task.type === 'email_auto_send' && <Send className="mr-2 h-4 w-4 text-green-500" />}
                                            <span className="text-sm">
                                              {task.type === 'task' && 'Tarefa'}
                                              {task.type === 'approval' && 'Aprovação'}
                                              {task.type === 'email_draft' && 'Email Rascunho'}
                                              {task.type === 'email_auto_send' && 'Email Auto'}
                                            </span>
                                          </div>
                                        </TableCell>
                                        <TableCell className="py-1 px-2">
                                          {task.assignees && task.assignees.length > 0 ? (
                                            <div className="flex items-center gap-1">
                                              <span className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs text-blue-700 border border-blue-200">
                                                {users.find(u => u.id === task.assignees[0])?.first_name?.charAt(0) || '?'}
                                              </span>
                                              <span className="text-sm truncate max-w-[120px]">
                                                {users.find(u => u.id === task.assignees[0])?.first_name || "Sem responsável"}
                                              </span>
                                            </div>
                                          ) : (
                                            <span className="text-sm text-muted-foreground">Sem responsável</span>
                                          )}
                                        </TableCell>
                                        <TableCell className="py-1 px-2">
                                          <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm">
                                              {task.deadline_value} dias após{' '}
                                              {task.deadline_reference === 'launch' ? 'início' : 
                                               task.deadline_reference === 'previous_task' ? 'tarefa anterior' : 
                                               'tarefa específica'}
                                            </span>
                                          </div>
                                        </TableCell>
                                        <TableCell className="p-1 pr-2">
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleRemoveTask(group.id, task.id)}
                                            className="h-7 w-7 opacity-0 group-hover/row:opacity-100 transition-opacity"
                                          >
                                            <Trash2 className="h-3.5 w-3.5" />
                                          </Button>
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                                {group.tasks.length === 0 && (
                                  <TableRow>
                                    <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                                      Nenhuma tarefa neste grupo.
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full mt-2 h-8 border border-dashed text-muted-foreground hover:text-foreground"
                            onClick={() => handleAddTask(group.id)}
                          >
                            <Plus className="mr-2 h-4 w-4" />
                            Adicionar Tarefa
                          </Button>
                        </div>
                      )}
                    </Droppable>
                  </CardContent>
                </Card>
              ))}
              
              {taskGroups.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="rounded-full bg-primary/10 p-3 mb-3">
                    <Plus className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-extrabold mb-1">Sem grupos de tarefas</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Crie um grupo para começar a adicionar tarefas ao template
                  </p>
                  <Button onClick={() => setShowAddGroupDialog(true)}>
                    Adicionar Grupo de Tarefas
                  </Button>
                </div>
              )}
            </DragDropContext>
          </div>
        </div>
        
        {/* Painel de propriedades (direita) */}
        <div className="w-80 border-l h-full overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-extrabold mb-3">
              {selectedTask 
                ? `Tarefa: ${selectedTask.task.title}` 
                : "Propriedades da Tarefa"}
            </h2>
            
            {selectedTask ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-xs font-extrabold mb-2">Básico</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs mb-1">Título</Label>
                      <Input
                        value={selectedTask.task.title}
                        onChange={(e) => handleUpdateTask(
                          selectedTask.groupId, 
                          selectedTask.taskId, 
                          "title", 
                          e.target.value
                        )}
                        className="h-8"
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Tipo de tarefa</Label>
                      <Select
                        value={selectedTask.task.type}
                        onValueChange={(value) => handleUpdateTask(
                          selectedTask.groupId, 
                          selectedTask.taskId, 
                          "type", 
                          value
                        )}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Selecione um tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="task">
                            <div className="flex items-center">
                              <CheckSquare className="mr-2 h-4 w-4 text-blue-500" />
                              <span>Tarefa Manual</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="approval">
                            <div className="flex items-center">
                              <Users className="mr-2 h-4 w-4 text-purple-500" />
                              <span>Aprovação</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="email_draft">
                            <div className="flex items-center">
                              <Mail className="mr-2 h-4 w-4 text-orange-500" />
                              <span>Rascunho de Email</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="email_auto_send">
                            <div className="flex items-center">
                              <Send className="mr-2 h-4 w-4 text-green-500" />
                              <span>Email Automático</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Responsável</Label>
                      <Select
                        value={selectedTask.task.assignees?.[0] || ""}
                        onValueChange={(value) => handleUpdateTask(
                          selectedTask.groupId, 
                          selectedTask.taskId, 
                          "assignees", 
                          [value]
                        )}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Escolha um responsável" />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.first_name} {user.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xs font-extrabold mb-2">Tempo</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs mb-1">Prazo</Label>
                      <div className="flex gap-2">
                        <Input 
                          type="number" 
                          min="1" 
                          className="w-20 h-8" 
                          value={selectedTask.task.deadline_value}
                          onChange={(e) => handleUpdateTask(
                            selectedTask.groupId, 
                            selectedTask.taskId, 
                            "deadline_value", 
                            parseInt(e.target.value) || 1
                          )}
                        />
                        <div className="flex-1">
                          <Select
                            value={selectedTask.task.deadline_reference}
                            onValueChange={(value) => handleUpdateTask(
                              selectedTask.groupId, 
                              selectedTask.taskId, 
                              "deadline_reference", 
                              value
                            )}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue placeholder="Após" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="launch">Início</SelectItem>
                              <SelectItem value="previous_task">Tarefa anterior</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xs font-extrabold mb-2">Conteúdo</h3>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs mb-1">Descrição</Label>
                      <Textarea 
                        placeholder="Adicione uma descrição" 
                        className="resize-none" 
                        rows={3}
                        value={selectedTask.task.description || ""}
                        onChange={(e) => handleUpdateTask(
                          selectedTask.groupId, 
                          selectedTask.taskId, 
                          "description", 
                          e.target.value
                        )}
                      />
                    </div>
                    <div>
                      <Label className="text-xs mb-1">Instruções detalhadas</Label>
                      <Textarea 
                        placeholder="Instruções para completar a tarefa" 
                        className="resize-none" 
                        rows={4}
                        value={selectedTask.task.detailed_description || ""}
                        onChange={(e) => handleUpdateTask(
                          selectedTask.groupId, 
                          selectedTask.taskId, 
                          "detailed_description", 
                          e.target.value
                        )}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xs font-extrabold mb-2">Campos Personalizados</h3>
                  <CustomFieldsEditor
                    customFields={selectedTask.task.custom_fields || []}
                    onCustomFieldsChange={(customFields) => 
                      handleUpdateCustomFields(
                        selectedTask.groupId, 
                        selectedTask.taskId, 
                        customFields
                      )
                    }
                  />
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Selecione uma tarefa para ver e editar suas propriedades.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Dialog para adicionar grupo */}
      <Dialog open={showAddGroupDialog} onOpenChange={setShowAddGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Grupo de Tarefas</DialogTitle>
            <DialogDescription>
              Crie um novo grupo para organizar suas tarefas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2">
              <Select
                value={newGroupIcon}
                onValueChange={setNewGroupIcon}
              >
                <SelectTrigger className="w-16">
                  <SelectValue placeholder={newGroupIcon} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="🎯">🎯</SelectItem>
                  <SelectItem value="📋">📋</SelectItem>
                  <SelectItem value="🔧">🔧</SelectItem>
                  <SelectItem value="📚">📚</SelectItem>
                  <SelectItem value="🚀">🚀</SelectItem>
                  <SelectItem value="⏱️">⏱️</SelectItem>
                  <SelectItem value="💬">💬</SelectItem>
                  <SelectItem value="📝">📝</SelectItem>
                  <SelectItem value="✅">✅</SelectItem>
                  <SelectItem value="🤝">🤝</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={newGroupTitle}
                onChange={(e) => setNewGroupTitle(e.target.value)}
                placeholder="Nome do grupo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddGroupDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddGroup}>Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}